/**
 * Helpers for surfacing a failed sandbox provision's step + diagnostics from the
 * instance's `healthCheckResults` (the canonical per-step failure record the
 * provision job writes for every failure path). Kept JSX-free so it's unit
 * testable in isolation.
 *
 * @module features/sandboxes/components/sandboxFailure
 */
import type { HealthCheckResult } from '../../../hooks/useEnvironmentInstances';

/**
 * Pull the failing step + diagnostics blob out of the health-check results.
 * The last `passed: false` entry is the one that stopped provisioning; its
 * `name` is the `<phase> · <step>` label and its `details` is the one-line
 * summary plus the captured diagnostics (compose `ps` + service logs /
 * deploy.log tail).
 */
export function getSandboxFailureDiagnostics(results?: HealthCheckResult[] | null): {
  step: string | null;
  diagnostics: string | null;
} {
  if (!results || results.length === 0) return { step: null, diagnostics: null };
  const failing = [...results].reverse().find((r) => !r.passed);
  if (!failing) return { step: null, diagnostics: null };
  return { step: failing.name, diagnostics: failing.details || null };
}
